// SAD-1005: Waiting on You, worded. One read, ListMyWork (slice 3), says what
// is the viewer's and ranks it on the server; its sources are (design §4):
//   - ListAttentionItems with owner "me": the late and at-risk dates it owns,
//     and the waiting approvals and requests on the programs it leads;
//   - ListMyAssignedActions: the corrections, issues and potential issues
//     assigned to it;
//   - AttentionService.ListAttention: what was shared with it (SAD-431);
//   - the pursuits it captures: pending go/no-go decisions and its tasks.
// Every verb opens the page that already does it.
import { contractDateISO } from '../../lib/contractDate';
import { badgeOf, verbOf, whatOf, whenOf } from '../Delivery/CommandCenter/attention';
import { dueWords } from '../Delivery/CommandCenter/listView';
import { addDays, cmDate } from '../Delivery/Deliverables/lib/format';
/**
 * The rank's buckets, in consequence order (design §3.3): dates past due,
 * then assigned work past due, then waiting approvals, requests, dates at
 * risk, open corrections, issues and potential issues, and shared items.
 */ export const WORK_KINDS = [
    'late',
    'overdue',
    'waiting',
    'decision',
    'request',
    'at_risk',
    'correction',
    'issue',
    'pursuit_task',
    'shared'
];
/** The tabs, "All" first; a tab with nothing in it is not offered. */ export const WORK_TABS = [
    {
        value: 'all',
        label: 'All'
    },
    {
        value: 'deliverables',
        label: 'Deliverables'
    },
    {
        value: 'requests',
        label: 'Requests'
    },
    {
        value: 'corrections',
        label: 'Corrections'
    },
    {
        value: 'pursuits',
        label: 'Pursuits'
    },
    {
        value: 'issues',
        label: 'Issues and risks'
    },
    {
        value: 'shared',
        label: 'Shared with you'
    }
];
const plural = (n, one, many = `${one}s`)=>`${n.toLocaleString()} ${n === 1 ? one : many}`;
const daysBetween = (from, to)=>Math.round((new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86400000);
const WHY = {
    team_owner: 'you own this team',
    program_lead: 'you lead this program'
};
/** A ListAttentionItems row, from the viewer's side. */ export function fromAttention(it, today) {
    if (![
        'late',
        'waiting',
        'request',
        'at_risk'
    ].includes(it.kind)) return null;
    const kind = it.kind;
    const when = whenOf(it, today);
    const why = WHY[it.why] ?? '';
    const where = kind === 'request' ? [
        it.programName,
        it.workstreamName,
        it.requestedBy && `requested by ${it.requestedBy}`,
        why
    ] : [
        it.programName,
        it.workstreamName || 'Contract-wide',
        why
    ];
    const name = [
        it.title || 'Deliverable',
        it.label
    ].filter(Boolean).join(' — ');
    // Many teams owe the same deliverable: the team tells two apart.
    const short = it.workstreamName ? `${name} for ${it.workstreamName}` : name;
    const verb = verbOf(it);
    return {
        key: `${kind}:${it.id}`,
        kind,
        tab: kind === 'request' ? 'requests' : 'deliverables',
        badge: badgeOf(it),
        what: whatOf(it),
        where: [
            'Delivery',
            ...where
        ].filter(Boolean).join(' · '),
        when: {
            ...when,
            urgent: kind === 'late'
        },
        dueOn: it.dueOn,
        overdueDays: kind === 'late' ? it.ageDays : 0,
        short,
        sub: it.workstreamName ? `${it.workstreamName} · ${it.programName}` : it.programName,
        program: it.programName,
        atRisk: kind === 'at_risk',
        // Late and waiting: the longest first; at risk: the soonest due; requests:
        // the server's order (the oldest first).
        urgency: kind === 'late' || kind === 'waiting' ? -it.ageDays : kind === 'at_risk' ? it.ageDays : it.rank,
        // A date is filed from its drawer.
        verb: kind === 'late' || kind === 'at_risk' ? {
            label: 'File',
            href: verb.href
        } : verb
    };
}
/** A due date in the queue's one wording (the Command Center's dueWords). */ function dueWhen(dueOn, today, none) {
    if (!dueOn) return {
        date: none,
        rel: '',
        urgent: false,
        overdueDays: 0
    };
    const days = daysBetween(dueOn, today);
    const w = dueWords(dueOn, today);
    return {
        date: `Due ${w.date}`,
        rel: w.rel,
        urgent: days > 0,
        overdueDays: Math.max(0, days)
    };
}
// Mirrors ryuguv1.AssignedActionKind (D8 renamed Risks to Potential Issues).
const ASSIGNED = {
    2: {
        label: 'Issue',
        noun: 'issue'
    },
    3: {
        label: 'Correction',
        noun: 'corrective action'
    },
    4: {
        label: 'Potential issue',
        noun: 'potential issue'
    }
};
const CORRECTION = 3;
const RESOLVABLE = new Set([
    'Open',
    'In Progress'
]);
/** A ListMyAssignedActions row: assigned to the viewer. */ export function fromAssigned(a, today) {
    const k = ASSIGNED[a.kind];
    if (!k) return null;
    const dueOn = contractDateISO(a.dueAt);
    const due = dueWhen(dueOn, today, 'No due date');
    const days = dueOn ? daysBetween(dueOn, today) : 0;
    const overdueDays = due.overdueDays;
    const correction = a.kind === CORRECTION;
    const resolvable = correction && RESOLVABLE.has(a.state);
    return {
        key: `assigned:${a.kind}:${a.id}`,
        kind: overdueDays > 0 ? 'overdue' : correction ? 'correction' : 'issue',
        tab: correction ? 'corrections' : 'issues',
        badge: overdueDays > 0 ? {
            label: `Overdue ${overdueDays.toLocaleString()}d`,
            tone: 'danger'
        } : {
            label: k.label,
            tone: correction ? 'warning' : 'info'
        },
        what: a.title || `Untitled ${k.noun}`,
        where: [
            'Delivery',
            a.programName,
            `${k.noun} assigned to you`
        ].filter(Boolean).join(' · '),
        when: {
            date: due.date,
            rel: due.rel,
            urgent: due.urgent
        },
        dueOn,
        overdueDays,
        short: `${k.noun} ${a.title}`.trim(),
        sub: a.programName,
        program: a.programName,
        atRisk: false,
        urgency: dueOn ? -days : Number.MAX_SAFE_INTEGER,
        // SAD-143: a correction never links into the program's audits page (the
        // assignee may hold no view on that program); it resolves in place.
        verb: correction ? resolvable ? {
            label: 'Resolve'
        } : undefined : a.destinationPath ? {
            label: 'Open',
            href: a.destinationPath
        } : undefined,
        resolve: resolvable ? a : undefined
    };
}
const SHARED_WHY = {
    LIBRARY_ITEM_RELEASED: 'a version of an item you own was released',
    LIBRARY_ITEM_WITHDRAWN: 'a release of an item you own was withdrawn'
};
/** An AttentionService item: shared with the viewer. */ export function fromShared(s, today) {
    const created = s.createdAt ? s.createdAt.toDate().toISOString().slice(0, 10) : '';
    const age = created ? Math.max(0, daysBetween(created, today)) : 0;
    return {
        key: `shared:${s.id}`,
        kind: 'shared',
        tab: 'shared',
        badge: {
            label: 'Shared',
            tone: 'info'
        },
        what: s.title || 'A shared item',
        where: [
            'Library',
            SHARED_WHY[s.notificationType] ?? s.body ?? 'shared with you'
        ].filter(Boolean).join(' · '),
        when: {
            date: created ? `Shared ${cmDate(created)}` : 'Shared',
            rel: created ? age === 0 ? 'today' : `${plural(age, 'day')} ago` : '',
            urgent: false
        },
        dueOn: '',
        overdueDays: 0,
        short: s.title,
        sub: 'Library',
        program: '',
        atRisk: false,
        // The newest first.
        urgency: age,
        verb: {
            label: 'Review',
            href: '/library'
        }
    };
}
const CAPTURE_WHY = {
    capture_manager: 'capture manager: you',
    team_owner: 'team owner: you'
};
const dayOfTs = (t)=>t ? t.toDate().toISOString().slice(0, 10) : '';
/** A pending go/no-go decision on a pursuit the viewer captures. */ export function fromDecision(d, today) {
    const due = dueWhen(dayOfTs(d.dueAt), today, 'No date set');
    return {
        key: `decision:${d.id}`,
        kind: due.overdueDays > 0 ? 'overdue' : 'decision',
        tab: 'pursuits',
        badge: due.overdueDays > 0 ? {
            label: `Overdue ${due.overdueDays.toLocaleString()}d`,
            tone: 'danger'
        } : {
            label: 'Go/no-go',
            tone: 'info'
        },
        what: `${d.pursuitName || 'Pursuit'}: go/no-go decision`,
        where: [
            'Pursuits',
            CAPTURE_WHY[d.why] ?? 'yours'
        ].join(' · '),
        when: {
            date: due.date,
            rel: due.rel,
            urgent: due.urgent
        },
        dueOn: dayOfTs(d.dueAt),
        overdueDays: due.overdueDays,
        short: `the go/no-go on ${d.pursuitName || 'a pursuit'}`,
        sub: 'Pursuits',
        program: d.pursuitName,
        atRisk: false,
        urgency: 0,
        verb: {
            label: 'Decide',
            href: `/capture/${encodeURIComponent(d.captureProjectId)}`
        }
    };
}
/** An open capture task the viewer owns. */ export function fromPursuitTask(t, today) {
    const due = dueWhen(dayOfTs(t.dueAt), today, 'No due date');
    return {
        key: `pursuit_task:${t.id}`,
        kind: due.overdueDays > 0 ? 'overdue' : 'pursuit_task',
        tab: 'pursuits',
        badge: due.overdueDays > 0 ? {
            label: `Overdue ${due.overdueDays.toLocaleString()}d`,
            tone: 'danger'
        } : {
            label: 'Task',
            tone: 'info'
        },
        what: t.title || 'Untitled task',
        where: [
            'Pursuits',
            t.pursuitName,
            'task owner: you'
        ].filter(Boolean).join(' · '),
        when: {
            date: due.date,
            rel: due.rel,
            urgent: due.urgent
        },
        dueOn: dayOfTs(t.dueAt),
        overdueDays: due.overdueDays,
        short: t.title,
        sub: t.pursuitName || 'Pursuits',
        program: t.pursuitName,
        atRisk: false,
        urgency: 0,
        verb: {
            label: 'Open',
            href: `/capture/${encodeURIComponent(t.captureProjectId)}`
        }
    };
}
/** One ListMyWork item, worded; the server's rank is kept by the caller. */ export function fromMyWorkItem(it, today) {
    if (it.attention) return fromAttention(it.attention, today);
    if (it.assigned) return fromAssigned(it.assigned, today);
    if (it.decision) return fromDecision(it.decision, today);
    if (it.pursuitTask) return fromPursuitTask(it.pursuitTask, today);
    if (it.shared) return fromShared(it.shared, today);
    return null;
}
/** The next n working days (Monday to Friday) from today, today first if it is one. */ export function workingDays(today, n = 5) {
    const out = [];
    for(let d = today; out.length < n; d = addDays(d, 1)){
        const wd = new Date(`${d}T00:00:00Z`).getUTCDay();
        if (wd !== 0 && wd !== 6) out.push(d);
    }
    return out;
}
/** Each of the next 5 working days with the viewer's items due on it, in rank order. */ export function agendaOf(ranked, today, days) {
    return (days?.length ? days : workingDays(today)).map((day)=>({
            day,
            items: ranked.filter((it)=>it.dueOn === day && it.overdueDays === 0)
        }));
}
/**
 * The Today band's verdict: everything waiting on the viewer, against the
 * target "nothing overdue". total and overdue count every item, the capped
 * reads' totals included (totals may exceed the items read).
 */ export function verdictOf(ranked, today, totals) {
    const days = workingDays(today);
    const lastDay = days[days.length - 1];
    const dueSoon = ranked.filter((it)=>it.dueOn && it.overdueDays === 0 && it.dueOn >= today && it.dueOn <= lastDay).length;
    const overdueItems = ranked.filter((it)=>it.overdueDays > 0);
    const named = overdueItems.slice(0, 2).map((it)=>`${it.short}${it.program ? ` on ${it.program}` : ''} (${plural(it.overdueDays, 'day')})`);
    const more = totals.overdue - named.length;
    let sentence = '';
    if (totals.overdue > 0 && named.length > 0) {
        const list = more > 0 ? `${named.join(', ')} and ${more.toLocaleString()} more` : named.length === 2 ? `${named[0]} and ${named[1]}` : named[0];
        sentence = `${totals.overdue === 1 ? '1 is' : `${totals.overdue.toLocaleString()} are`} overdue: ${list}.`;
    }
    return {
        total: totals.total,
        overdue: totals.overdue,
        dueSoon,
        lastDay,
        sentence,
        tone: totals.total === 0 ? 'neutral' : totals.overdue > 0 ? 'danger' : 'success'
    };
}
/** "a filing waiting 3 days, a pursuit task, …": what "Show N more" would show. */ export function hiddenWords(hidden) {
    const NOUN = {
        late: [
            'late deliverable',
            'late deliverables'
        ],
        overdue: [
            'overdue action',
            'overdue actions'
        ],
        waiting: [
            'filing waiting on approval',
            'filings waiting on approval'
        ],
        request: [
            'request',
            'requests'
        ],
        at_risk: [
            'deliverable at risk',
            'deliverables at risk'
        ],
        correction: [
            'correction',
            'corrections'
        ],
        issue: [
            'issue or risk',
            'issues and risks'
        ],
        decision: [
            'go/no-go decision',
            'go/no-go decisions'
        ],
        pursuit_task: [
            'pursuit task',
            'pursuit tasks'
        ],
        shared: [
            'item shared with you',
            'items shared with you'
        ]
    };
    const n = new Map();
    for (const h of hidden)n.set(h.kind, (n.get(h.kind) ?? 0) + 1);
    return WORK_KINDS.filter((k)=>n.has(k)).map((k)=>{
        const c = n.get(k) ?? 0;
        const noun = NOUN[k][c === 1 ? 0 : 1];
        return `${c === 1 ? /^[aeiou]/.test(noun) ? 'an' : 'a' : c.toLocaleString()} ${noun}`;
    }).join(', ');
}
